export function ticker() {
    const tickerWrappers = document.querySelectorAll('.ticker__wrapper');

    if (!tickerWrappers.length) {
        return;
    }

    const cleanupFunctions = [];

    const setupTickerInstance = (tickerWrapper) => {
        const tickerList = tickerWrapper?.querySelector('ul');

        if (!tickerWrapper || !tickerList) {
            return;
        }

        // Prevent multiple initializations per wrapper
        if (tickerWrapper.dataset.initialized === 'true') {
            return;
        }
        tickerWrapper.dataset.initialized = 'true';

        let animation = null;
        let timeScaleAnimation = null;
        let timeScaleObj = null; // Store the timeScale object to access current value
        let resizeTimeout = null;
        let isInitialized = false;
        let isHovering = false;
        const pixelsPerSecond = 70; // Base speed in pixels per second
        const hoverSpeedMultiplier = 0.67; // Speed multiplier on hover (slower)
        const speedUpDuration = 1.5; // Duration in seconds for gradual speed increase
        const MAX_GSAP_RETRIES = 50; // Maximum retries for GSAP (10 seconds total)
        let gsapRetryCount = 0;

        // Store event handlers for cleanup
        const handlers = {
            resize: null,
            mouseenter: null,
            mouseleave: null
        };

        // Robust GSAP waiting mechanism
        const waitForGSAP = (callback) => {
            const gsapLib = window.gsap || (typeof gsap !== 'undefined' ? gsap : null);
            
            if (gsapLib) {
                callback(gsapLib);
                return;
            }

            gsapRetryCount++;
            if (gsapRetryCount >= MAX_GSAP_RETRIES) {
                console.warn('GSAP not found after maximum retries');
                return;
            }

            // Use requestAnimationFrame for better timing
            requestAnimationFrame(() => {
                waitForGSAP(callback);
            });
        };

        // Wait for styles to be applied using requestAnimationFrame
        const waitForStyles = (callback, maxAttempts = 20) => {
            let attempts = 0;
            
            const checkStyles = () => {
                const testWidth = tickerWrapper.offsetWidth;
                const firstItem = tickerList.querySelector('li:not([data-cloned="true"])');
                
                if (testWidth > 0 && firstItem && firstItem.offsetWidth > 0) {
                    callback();
                } else if (attempts < maxAttempts) {
                    attempts++;
                    requestAnimationFrame(checkStyles);
                } else {
                    // Fallback: proceed anyway after max attempts
                    callback();
                }
            };
            
            requestAnimationFrame(checkStyles);
        };

        const originalItems = tickerList.querySelectorAll('li:not([data-cloned="true"])');
        
        if (!originalItems.length) {
            return;
        }

        // Calculate total width of original items with validation
        const getOriginalWidth = () => {
            let totalWidth = 0;
            let hasValidWidth = false;
            
            originalItems.forEach(item => {
                const width = item.offsetWidth || item.getBoundingClientRect().width;
                if (width > 0) {
                    hasValidWidth = true;
                }
                totalWidth += width;
            });
            
            return hasValidWidth ? totalWidth : 0;
        };

        // Clone items until they fill the screen width
        const cloneItemsUntilFull = () => {
            const wrapperWidth = tickerWrapper.offsetWidth || window.innerWidth;
            const originalWidth = getOriginalWidth();
            
            if (originalWidth === 0) {
                return false;
            }
            
            // Remove any existing cloned items
            const clonedItems = tickerList.querySelectorAll('li[data-cloned="true"]');
            clonedItems.forEach(item => item.remove());
            
            // Clone enough sets to cover at least 2x wrapper width
            const itemsArray = Array.from(originalItems);
            const setsNeeded = Math.max(2, Math.ceil((wrapperWidth * 2) / originalWidth) + 1);
            
            for (let i = 0; i < setsNeeded; i++) {
                itemsArray.forEach(item => {
                    const clone = item.cloneNode(true);
                    clone.setAttribute('data-cloned', 'true');
                    tickerList.appendChild(clone);
                });
            }
            
            return true;
        };

        const initAnimation = (gsapLib) => {
            const originalWidth = getOriginalWidth();
            
            if (originalWidth === 0) {
                return false;
            }

            // Kill previous animation if it exists
            if (animation) {
                animation.kill();
                animation = null;
            }

            // Reset position to start
            gsapLib.set(tickerList, { x: 0, clearProps: 'transform' });

            // Calculate duration based on constant speed
            // Duration = distance / speed (in seconds)
            const baseDuration = originalWidth / pixelsPerSecond;
            
            // Create seamless infinite loop
            const animateLoop = () => {
                // Get current position before creating animation
                const currentX = parseFloat(gsapLib.getProperty(tickerList, "x") || 0);
                
                animation = gsapLib.to(tickerList, {
                    x: currentX - originalWidth,
                    duration: baseDuration,
                    ease: 'none',
                    force3D: true,
                    onComplete: () => {
                        // Reset position seamlessly by adjusting relative to current position
                        // Get current transform value and add originalWidth to it
                        // This prevents visible jump because we're adjusting, not jumping to 0
                        const currentX = parseFloat(gsapLib.getProperty(tickerList, "x") || 0);
                        gsapLib.set(tickerList, { x: currentX + originalWidth, force3D: true });
                        // Immediately start next loop to maintain continuity
                        animateLoop();
                    }
                });

                // Set initial timeScale based on current state
                // If timeScaleAnimation is still running, start at 0 for gradual increase
                // Otherwise, use the final value (1 or hover-adjusted)
                if (timeScaleObj && timeScaleAnimation && timeScaleAnimation.isActive()) {
                    // Speed-up animation is still running, start at 0
                    animation.timeScale(0);
                } else {
                    // Speed-up animation has completed, use final value
                    const baseTimeScale = timeScaleObj ? timeScaleObj.value : 1;
                    const currentTimeScale = isHovering ? baseTimeScale * hoverSpeedMultiplier : baseTimeScale;
                    animation.timeScale(currentTimeScale);
                }
            };

            // Start the loop
            animateLoop();
            return true;
        };

        // Main initialization function
        const init = (gsapLib) => {
            if (isInitialized) {
                return;
            }

            // Wait for styles, then clone and animate
            waitForStyles(() => {
                if (!cloneItemsUntilFull()) {
                    // Retry if cloning failed
                    requestAnimationFrame(() => {
                        if (cloneItemsUntilFull()) {
                            requestAnimationFrame(() => {
                                // Wait for DOM to update with clones
                                requestAnimationFrame(() => {
                                    if (initAnimation(gsapLib)) {
                                        // Create timeScale animation for gradual speed increase
                                        timeScaleObj = { value: 0 };
                                        timeScaleAnimation = gsapLib.to(timeScaleObj, {
                                            value: 1,
                                            duration: speedUpDuration,
                                            ease: 'power2.out',
                                            onUpdate: () => {
                                                if (animation) {
                                                    // Apply hover multiplier if hovering
                                                    const currentTimeScale = isHovering 
                                                        ? timeScaleObj.value * hoverSpeedMultiplier 
                                                        : timeScaleObj.value;
                                                    animation.timeScale(currentTimeScale);
                                                }
                                            },
                                            onComplete: () => {
                                                // Ensure final timeScale is set after animation completes
                                                if (animation) {
                                                    const finalTimeScale = isHovering 
                                                        ? timeScaleObj.value * hoverSpeedMultiplier 
                                                        : timeScaleObj.value;
                                                    animation.timeScale(finalTimeScale);
                                                }
                                            }
                                        });
                                        
                                        isInitialized = true;
                                    }
                                });
                            });
                        }
                    });
                    return;
                }

                // Wait for cloned items to render
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (initAnimation(gsapLib)) {
                                // Create timeScale animation for gradual speed increase
                                timeScaleObj = { value: 0 };
                                timeScaleAnimation = gsapLib.to(timeScaleObj, {
                                    value: 1,
                                    duration: speedUpDuration,
                                    ease: 'power2.out',
                                    onUpdate: () => {
                                        if (animation) {
                                            // Apply hover multiplier if hovering
                                            const currentTimeScale = isHovering 
                                                ? timeScaleObj.value * hoverSpeedMultiplier 
                                                : timeScaleObj.value;
                                            animation.timeScale(currentTimeScale);
                                        }
                                    },
                                    onComplete: () => {
                                        // Ensure final timeScale is set after animation completes
                                        if (animation) {
                                            const finalTimeScale = isHovering 
                                                ? timeScaleObj.value * hoverSpeedMultiplier 
                                                : timeScaleObj.value;
                                            animation.timeScale(finalTimeScale);
                                        }
                                    }
                                });
                                
                                isInitialized = true;
                            }
                        });
                    });
                });
            });
        };

        // Setup resize handler
        handlers.resize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (animation) {
                    animation.kill();
                    animation = null;
                }
                if (timeScaleAnimation) {
                    timeScaleAnimation.kill();
                    timeScaleAnimation = null;
                }
                isHovering = false;
                timeScaleObj = null;
                
                waitForStyles(() => {
                    if (cloneItemsUntilFull()) {
                        requestAnimationFrame(() => {
                            waitForGSAP((gsapLib) => {
                                if (initAnimation(gsapLib)) {
                                    // Create timeScale animation for gradual speed increase
                                    timeScaleObj = { value: 0 };
                                    timeScaleAnimation = gsapLib.to(timeScaleObj, {
                                        value: 1,
                                        duration: speedUpDuration,
                                        ease: 'power2.out',
                                        onUpdate: () => {
                                            if (animation) {
                                                // Apply hover multiplier if hovering
                                                const currentTimeScale = isHovering 
                                                    ? timeScaleObj.value * hoverSpeedMultiplier 
                                                    : timeScaleObj.value;
                                                animation.timeScale(currentTimeScale);
                                            }
                                        },
                                        onComplete: () => {
                                            // Ensure final timeScale is set after animation completes
                                            if (animation) {
                                                const finalTimeScale = isHovering 
                                                    ? timeScaleObj.value * hoverSpeedMultiplier 
                                                    : timeScaleObj.value;
                                                animation.timeScale(finalTimeScale);
                                            }
                                        }
                                    });
                                }
                            });
                        });
                    }
                });
            }, 250);
        };

        // Setup hover handlers
        handlers.mouseenter = () => {
            isHovering = true;
            if (animation) {
                // Get current timeScale value (default to 1 if timeScaleObj is null/completed)
                const baseTimeScale = timeScaleObj ? timeScaleObj.value : 1;
                const currentTimeScale = baseTimeScale * hoverSpeedMultiplier;
                animation.timeScale(currentTimeScale);
            }
        };

        handlers.mouseleave = () => {
            isHovering = false;
            if (animation) {
                // Get current timeScale value (default to 1 if timeScaleObj is null/completed)
                const baseTimeScale = timeScaleObj ? timeScaleObj.value : 1;
                animation.timeScale(baseTimeScale);
            }
        };

        // Attach event listeners
        window.addEventListener('resize', handlers.resize);
        tickerWrapper.addEventListener('mouseenter', handlers.mouseenter);
        tickerWrapper.addEventListener('mouseleave', handlers.mouseleave);

        // Start initialization
        waitForGSAP((gsapLib) => {
            // Ensure DOM is ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                    requestAnimationFrame(() => {
                        init(gsapLib);
                    });
                });
            } else {
                requestAnimationFrame(() => {
                    init(gsapLib);
                });
            }
        });

        // Cleanup function for this instance
        return () => {
            if (animation) {
                animation.kill();
                animation = null;
            }
            if (timeScaleAnimation) {
                timeScaleAnimation.kill();
                timeScaleAnimation = null;
            }
            
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            window.removeEventListener('resize', handlers.resize);
            tickerWrapper.removeEventListener('mouseenter', handlers.mouseenter);
            tickerWrapper.removeEventListener('mouseleave', handlers.mouseleave);
            
            tickerWrapper.dataset.initialized = 'false';
            isInitialized = false;
        };
    };

    tickerWrappers.forEach((wrapper) => {
        const cleanup = setupTickerInstance(wrapper);
        if (typeof cleanup === 'function') {
            cleanupFunctions.push(cleanup);
        }
    });

    if (!cleanupFunctions.length) {
        return;
    }

    // Combined cleanup for all instances
    return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
    };
}

